import { escapeHtml, safeUrl } from '../ui.js';
import { listFolder, isFolderMime, driveThumbnail, binDriveFile, unbinDriveFile, driveWriteToken } from '../google-api.js';
import { folderCode, sizeText } from '../drive-folders.js';
import { docState, isIgnoredFile, CHECKED_FOLDERS } from '../doc-convention.js';
import { openFile } from './google-panels.js';
import { swr, driveKey, ageText, driveStore } from '../drive-cache.js';
import { startDriveLive } from '../drive-live.js';
import { watchFileUse, FILE_USE_STALE_MS, registerRowsForFiles, setRegisterStatus } from '../db.js';
import { navIcon } from '../nav-icons.js';
import { openRowMenu } from './row-menu.js';
import { openEndingConfirm } from './ending-confirm.js';
import { createBulkBar, checkboxCell } from './bulk-bar.js';
import { moveToast } from './cal-drag.js';
import { binGuard, binId, rowKey, binCopy, binToastText, countFolder, moveToBin, registerBlock, refusalText } from '../files-bin.js';

// A FOLDER, OPENED IN TRACK (Karan 06/10/2026: "When I get into a project
// folder, it just opens google drive? Why?"). v4.0.0 (design export p15):
//
//   breadcrumb   Shared drives / PROJECTS / 8588 Hart Gardens — retaining wall
//   table        NAME · KIND · MODIFIED · BY · STATUS — folders first, then files
//   preview      (Files only, opts.preview) a file row selects it: Drive's
//                thumbnail, its facts, Open; a double-click opens it at once
//
// Every folder in Files — a project folder, its numbered folders, a folder on
// any other drive — opens here. A file opens in Track's own viewers (the
// drawing reviewer for PDFs, the CAD/IFC/Tedds viewers, Drive's preview for the
// rest). Google Drive is one explicit link away ("Open in Drive"), never the
// default.
//
// Inside a project's numbered folder each file also carries its document-
// control state against the house numbering rule (Current / Superseded / the
// rename it needs), and the team's advisory "In use" mark (fileUse — it warns,
// it never locks).
//
// MOVE TO BIN (v4.23.10, files-bin.js): a ⋯ on every row (Open, Open in Drive,
// Select, then the ending "Move to bin"), a checkbox per row that swaps in for
// the icon on hover (always on touch; shift-click selects a range) with the
// bulk bar's "Move N to bin", and "Move to bin" in the preview panel. Every one
// opens the same ending confirm and ends in a "Moved to bin · Undo" toast. It
// is Drive's bin, with the person's own token — never a delete.
//
// The listing paints from what Drive said last time (drive-cache.js) and is
// read again behind it. Every value from Drive is escaped; every href goes
// through safeUrl().

const pad2 = (n) => String(n).padStart(2, '0');
const skel = (w, h) => `<span class="fm-skel" style="width:${w};height:${h};"></span>`;
const thumbs = new Map();

/** 22/10, or "today" (p15). */
function dayShort(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'today';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}${d.getFullYear() === now.getFullYear() ? '' : `/${String(d.getFullYear()).slice(-2)}`}`;
}

const KINDS = { pdf: 'PDF', ted: 'Tedds', dwg: 'DWG', dxf: 'DXF', zip: 'Archive', rar: 'Archive', '7z': 'Archive', jpg: 'Image', jpeg: 'Image', png: 'Image', heic: 'Image',
  doc: 'Word', docx: 'Word', xls: 'Excel', xlsx: 'Excel', csv: 'CSV', ifc: 'IFC', tsmd: 'Masonry', eml: 'Email', msg: 'Email', txt: 'Text' };
const GOOGLE = { 'application/vnd.google-apps.document': 'Google Doc', 'application/vnd.google-apps.spreadsheet': 'Google Sheet', 'application/vnd.google-apps.presentation': 'Google Slides' };
/** What a Drive item is, in a word: Folder, PDF, Tedds, Archive… (from its type, else its extension). */
export function kindOf(f) {
  if (!f) return '';
  if (isFolderMime(f.mimeType)) return 'Folder';
  if (GOOGLE[f.mimeType]) return GOOGLE[f.mimeType];
  const ext = (String(f.name || '').match(/\.([A-Za-z0-9]{1,5})$/) || [, ''])[1].toLowerCase();
  return KINDS[ext] || (ext ? ext.toUpperCase() : 'File');
}
/** The revision in a house-numbered name: "8588-04-0102_A.pdf" → "A". */
const revOf = (name) => (String(name || '').match(/_([A-Z]{1,2}|-)(?=[\s.])/) || [, ''])[1];
const firstName = (s) => String(s || '').trim().split(/\s+/)[0] || '';
const REVIEWER_ROLES = ['Owner', 'Director', 'Finance', 'Coordinator'];   // firestore.rules reviewer(): who may update a register row

/**
 * opts:
 *   session, setHero(h)   h.driveHref: where "Open in Drive" should go now
 *   trail    [{ id, name, webViewLink }] — the folders from the entry point down;
 *            the last one is shown
 *   onTrail  (trail) => void — told the trail on every move (optional)
 *   before   [{ label, onClick }] — the crumbs before the trail (Shared drives / PROJECTS)
 *   home     { label, onClick } — one crumb before the trail (older callers)
 *   project  '8593' or null — for document control, "In use" and the reviewer
 *   codeAt   index in `trail` of the project's numbered folder, or -1 (found
 *            below a project folder as you go in)
 *   preview  true: a file row selects it into the preview panel (Files)
 *   onCheckJob (number) — "Check 8593's numbering" on a project folder
 *   driveInHead  true: the page head carries "Open in Drive" (Files), so the crumbs don't
 */
export async function renderFolderView(body, opts) {
  const { session, setHero = () => {}, trail = [], project = null, preview = false } = opts;
  const before = opts.before || (opts.home ? [opts.home] : []);
  const here = trail[trail.length - 1];
  if (!here) return;
  body.__folderId = here.id;   // the live feed checks it is still this folder
  opts.onTrail?.(trail);       // the caller keeps where you are for a refresh
  // The numbered folder: given, or the first numbered folder below a project folder.
  const codeAt = opts.codeAt >= 0 ? opts.codeAt : (project ? trail.findIndex((n, i) => i > 0 && folderCode(n.name)) : -1);
  const code = codeAt >= 0 && trail[codeAt] ? folderCode(trail[codeAt].name) : null;
  const rel = code ? trail.slice(codeAt + 1).map((n) => n.name) : [];
  const withDc = Boolean(project && code && CHECKED_FOLDERS.includes(code));
  const go = (t) => renderFolderView(body, { ...opts, trail: t, codeAt: opts.codeAt });
  const driveHref = safeUrl(here.webViewLink || `https://drive.google.com/drive/folders/${encodeURIComponent(here.id)}`);
  setHero({ driveHref });

  const crumbs = `<nav class="crumbs fl4-crumbs" aria-label="Folder path">
      ${before.map((b, i) => `<button type="button" data-before="${i}">${escapeHtml(b.label)}</button><span aria-hidden="true">/</span>`).join('')}
      ${trail.map((n, i) => (i < trail.length - 1
        ? `<button type="button" data-crumb="${i}">${escapeHtml(n.name)}</button><span aria-hidden="true">/</span>`
        : `<span class="fl4-here" aria-current="page">${escapeHtml(n.name)}</span>`)).join('')}
      <span class="fl4-crumb-end">
        <span class="fl4-note" id="fx-fold-note"></span>
        ${opts.onCheckJob && project && trail.length === 1 ? `<button type="button" class="fl4-link" id="fx-check-job">Check ${escapeHtml(project)}’s numbering</button>` : ''}
        ${opts.driveInHead ? '' : `<a class="fl4-link" href="${escapeHtml(driveHref)}" target="_blank" rel="noopener">Open in Drive</a>`}
      </span>
    </nav>`;
  const cols = '<div class="fl4-row fl4-cols"><span>Name</span><span>Kind</span><span>Modified</span><span>By</span><span>Status</span><span class="fl4-more" aria-hidden="true"></span></div>';
  const shell = (rows) => `<div class="fl4-folder${preview ? ' has-pane' : ''}">
      <div class="fl4-main">${crumbs}<div class="fl4-table" role="grid">${cols}${rows}</div></div>
      ${preview ? '<aside class="fl4-pane" aria-label="Preview"></aside>' : ''}
    </div>`;
  body.innerHTML = shell([0, 1, 2, 3].map(() => `<div class="fl4-row">${skel('50%', '13px')}${skel('40px', '12px')}${skel('40px', '12px')}${skel('50px', '12px')}<span></span><span></span></div>`).join(''));
  wireNav();

  function wireNav() {
    body.querySelectorAll('[data-crumb]').forEach((c) => c.addEventListener('click', () => go(trail.slice(0, Number(c.dataset.crumb) + 1))));
    body.querySelectorAll('[data-before]').forEach((c) => c.addEventListener('click', () => { const b = before[Number(c.dataset.before)]; if (b) b.onClick(); }));
    const check = body.querySelector('#fx-check-job');
    if (check) check.addEventListener('click', () => opts.onCheckJob(project));
  }

  const listKey = driveKey(session && session.email, 'list', here.id);
  // The team's advisory "In use" marks for this project (who has a file open).
  let use = new Map();
  let unwatch = null;
  if (project && withDc) {
    unwatch = watchFileUse(project, (list) => {
      if (!body.isConnected || body.__folderId !== here.id) { if (unwatch) unwatch(); return; }
      use = new Map(list.map((u) => [u.fileId, u]));
      if (items) paint(items, lastMeta || {});
    });
  }
  const useOf = (f) => {
    const u = use.get(f.id);
    if (!u) return null;
    const d = u.since && u.since.toDate ? u.since.toDate() : null;
    return d && Date.now() - d.getTime() > FILE_USE_STALE_MS ? null : u;
  };

  // ── Move to bin: the selection (the bulk bar) and the guard ──
  const reviewer = REVIEWER_ROLES.some((r) => ((session && session.roles) || []).includes(r));
  const guardOf = (f) => binGuard(f, { parentName: here.name, inUse: useOf });
  // A project or template folder is never selectable: no checkbox at all.
  const protectedRow = (f) => { const g = guardOf(f); return !g.ok && (g.tag === 'Project folder' || g.tag === 'Template folder'); };
  if (body.__bulk) body.__bulk.clear();   // a previous folder's selection never carries over
  let anchor = null;                      // the last checkbox clicked (shift-click range)
  const bulk = createBulkBar(body, {
    sticky: true,
    actions: [{ label: (n) => `Move ${n} to bin`, onRun: (keys) => startBin(keys.map(byKey).filter(Boolean)) }],
    onSelectionChange: (set) => markPicked(set),
  });
  body.__bulk = bulk;
  const byKey = (k) => (items || []).find((f) => rowKey(f) === k) || null;
  function markPicked(set) {
    const table = body.querySelector('.fl4-table');
    if (table) table.classList.toggle('is-picking', set.size > 0);
    body.querySelectorAll('.fl4-row[data-key]').forEach((r) => {
      const on = set.has(r.dataset.key);
      r.classList.toggle('is-picked', on);
      const c = r.querySelector('.fl4-check');
      if (c) c.innerHTML = checkboxCell(r.dataset.key, on);
    });
  }
  // The icon, or (on hover, on touch, or while picking) the checkbox in its place.
  const pickSlot = (f, icon) => (protectedRow(f)
    ? icon
    : `<span class="fl4-pick"><span class="fl4-ico">${icon}</span><span class="fl4-check">${checkboxCell(rowKey(f), bulk.isSelected(rowKey(f)))}</span></span>`);
  const moreBtn = (f) => `<span class="fl4-more"><button type="button" class="fl4-more-btn" data-more="${escapeHtml(rowKey(f))}" aria-label="More for ${escapeHtml(f.name)}" aria-haspopup="menu">⋯</button></span>`;

  let items = null;
  let lastMeta = null;
  let sel = null;   // the selected file's id (preview)
  let dirs = [];
  let files = [];
  let states = [];
  const statusHtml = (f, s) => {
    const u = useOf(f);
    const chips = [];
    if (u) chips.push(`<span class="fl4-chip is-late" title="In use${u.app ? ` in ${escapeHtml(u.app)}` : ''} — a warning, never a lock">In use · ${escapeHtml(firstName(u.by) || 'someone')}</span>`);
    if (s && s.tone === 'grey') chips.push('<span class="fl4-chip is-grey">Superseded</span>');
    else if (s && s.tone === 'ok') chips.push('<span class="fl4-chip is-ok">Current</span>');
    else if (s && s.tone === 'attn') chips.push(`<span class="fl4-chip is-late" title="${escapeHtml(s.label)}${s.suggestion && s.suggestion.name ? ` → ${escapeHtml(s.suggestion.name)}` : ''}">Rename</span>`);
    return chips.join('');
  };
  const paint = (list, meta) => {
    if (!body.isConnected) return;
    lastMeta = meta;
    items = list.filter((f) => !isIgnoredFile(f.name));
    dirs = items.filter((f) => isFolderMime(f.mimeType)).sort((a, b) => String(a.name).localeCompare(String(b.name), 'en-GB', { numeric: true }));
    files = items.filter((f) => !isFolderMime(f.mimeType)).sort((a, b) => String(a.name).localeCompare(String(b.name), 'en-GB', { numeric: true }));
    const names = files.map((f) => f.name);
    states = files.map((f) => (withDc ? docState(f.name, { project, code, rel, siblings: names }) : null));
    const note = meta.cached && meta.revalidating ? `What Drive showed ${ageText(meta.at)} · checking again` : '';
    const by = (f) => escapeHtml(firstName(f.lastModifyingUser && f.lastModifyingUser.displayName));
    const picked = (f) => (bulk.isSelected(rowKey(f)) ? ' is-picked' : '');
    const rowsDirs = dirs.map((f, i) => `<div class="fl4-row${picked(f)}" data-go="dir" data-i="${i}" data-key="${escapeHtml(rowKey(f))}" role="row" tabindex="0">
        <span class="fl4-name is-dir">${pickSlot(f, navIcon('projects', 16))}<span class="fl4-t">${escapeHtml(f.name)}</span></span>
        <span>Folder</span><span class="num-tech">${escapeHtml(dayShort(f.modifiedTime))}</span><span class="fl4-t">${by(f)}</span><span></span>${moreBtn(f)}
      </div>`).join('');
    const rowsFiles = files.map((f, i) => {
      const s = states[i];
      return `<div class="fl4-row${sel === f.id ? ' is-sel' : ''}${s && s.tone === 'grey' ? ' is-dim' : ''}${picked(f)}" data-go="file" data-i="${i}" data-key="${escapeHtml(rowKey(f))}" role="row" tabindex="0" aria-selected="${sel === f.id}">
          <span class="fl4-name">${pickSlot(f, navIcon('files', 16))}<span class="fl4-stack"><span class="fl4-t num-tech" title="${escapeHtml(f.name)}">${escapeHtml(f.name)}</span>${s && s.suggestion && s.suggestion.name ? `<span class="fl4-sub num-tech">→ ${escapeHtml(s.suggestion.name)}</span>` : ''}</span></span>
          <span>${escapeHtml(kindOf(f))}</span><span class="num-tech">${escapeHtml(dayShort(f.modifiedTime))}</span><span class="fl4-t">${by(f)}</span>
          <span class="fl4-status">${statusHtml(f, s)}</span>${moreBtn(f)}
        </div>`;
    }).join('');
    const scroll = body.querySelector('.fl4-table') ? window.scrollY : null;
    body.innerHTML = shell(`${rowsDirs}${rowsFiles || (dirs.length ? '' : '<div class="fl4-empty">This folder is empty.</div>')}`);
    if (scroll != null) window.scrollTo(0, scroll);
    // A selected row that has gone (binned, or moved in Drive) leaves the selection.
    const present = new Set(items.map(rowKey));
    const keep = bulk.selected().filter((k) => present.has(k));
    if (keep.length !== bulk.selected().length) bulk.setAll(keep); else { bulk.render(); markPicked(new Set(keep)); }
    const n = body.querySelector('#fx-fold-note'); if (n) n.textContent = note;
    wireNav();
    if (preview) paintPane();
    body.querySelectorAll('.fl4-row[data-go]').forEach((r) => {
      // A shift-click picks a range; it must not also select the page's text.
      r.addEventListener('mousedown', (e) => { if (e.shiftKey && (e.target.closest('[data-bulk-id]') || bulk.selected().length)) e.preventDefault(); });
      r.addEventListener('click', (e) => {
        if (e.target.closest('.fl4-more-btn')) { e.stopPropagation(); rowMenu(r, e.target.closest('.fl4-more-btn')); return; }
        if (e.target.closest('[data-bulk-id]')) { e.stopPropagation(); pick(r.dataset.key, e.shiftKey); return; }
        // While picking, a shift-click on a row extends the range too.
        if (e.shiftKey && bulk.selected().length && r.dataset.key && !protectedRow(byKey(r.dataset.key))) { e.preventDefault(); pick(r.dataset.key, true); return; }
        if (r.dataset.go === 'file' && preview) select(r); else open(r);
      });
      r.addEventListener('dblclick', (e) => { if (e.target.closest('.fl4-more-btn, [data-bulk-id]')) return; open(r); });
      r.addEventListener('keydown', (e) => {
        if (e.target.closest('[data-bulk-id]') && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); e.stopPropagation(); pick(r.dataset.key, e.shiftKey); return; }
        if (e.target.closest('.fl4-more-btn')) return;   // the button's own Enter/Space opens the menu
        if (e.key === 'Enter') { e.preventDefault(); open(r); } else if (e.key === ' ' && preview && r.dataset.go === 'file') { e.preventDefault(); select(r); } });
    });
  };

  const reviewerFor = (g, k) => ({ reference: g.name, title: g.name, status: states[k] && states[k].label === 'Superseded' ? 'Superseded' : 'Current',
    user: (session && (session.displayName || session.email)) || '', email: (session && session.email) || null, projectNo: String(project || '') });
  function openOne(f) {
    const i = files.indexOf(f);
    openFile(f, { reviewer: reviewerFor(f, i), reviewerFor, siblings: files });
  }
  function open(el) {
    const i = Number(el.dataset.i);
    if (el.dataset.go === 'dir') { const f = dirs[i]; go([...trail, { id: f.id, name: f.name, webViewLink: f.webViewLink }]); return; }
    openOne(files[i]);
  }
  function select(el) {
    const f = files[Number(el.dataset.i)];
    if (!f) return;
    sel = f.id;
    body.querySelectorAll('.fl4-row[data-go="file"]').forEach((r) => { const on = files[Number(r.dataset.i)] === f; r.classList.toggle('is-sel', on); r.setAttribute('aria-selected', String(on)); });
    paintPane();
  }

  // ── Move to bin ──
  // A checkbox click toggles; shift-click selects every row from the last one
  // clicked to this one (folders then files, as listed). Protected rows have no
  // checkbox and are skipped by a range.
  function pick(key, shift) {
    if (!key) return;
    const order = [...dirs, ...files].filter((f) => !protectedRow(f)).map(rowKey);
    if (shift && anchor && order.includes(anchor) && order.includes(key)) {
      const [a, b] = [order.indexOf(anchor), order.indexOf(key)].sort((x, y) => x - y);
      bulk.setAll([...new Set([...bulk.selected(), ...order.slice(a, b + 1)])]);
    } else bulk.toggle(key);
    anchor = key;
  }
  // The row menu (6c): Open first, the ending last behind its hairline.
  function rowMenu(r, btn) {
    const f = byKey(r.dataset.key);
    if (!f) return;
    const g = guardOf(f);
    const menu = openRowMenu(btn, [
      { label: 'Open', onClick: () => open(r) },
      f.webViewLink ? { label: 'Open in Drive', onClick: () => window.open(safeUrl(f.webViewLink), '_blank', 'noopener') } : null,
      protectedRow(f) ? null : { label: bulk.isSelected(rowKey(f)) ? 'Unselect' : 'Select', onClick: () => pick(rowKey(f), false) },
      { label: 'Move to bin', ending: true, role: g.ok ? '' : g.tag, allowed: g.ok, onClick: () => startBin([f]) },
    ].filter(Boolean));
    // Keep the ⋯ showing while its menu is open (the pointer has left the row).
    btn.setAttribute('aria-expanded', 'true');
    const obs = new MutationObserver(() => { if (!menu.isConnected) { btn.setAttribute('aria-expanded', 'false'); obs.disconnect(); } });
    obs.observe(document.body, { childList: true });
  }
  // The listing again, straight from Drive (after a move or an undo).
  async function reload() {
    try {
      const list = await listFolder(here.id);
      if (!body.isConnected || body.__folderId !== here.id) return;
      const at = Date.now();
      driveStore.set(listKey, { value: list, at }).catch(() => {});
      paint(list, { cached: false, at });
    } catch (_) { /* the live feed catches up within ~30 s */ }
  }

  // Everything that has to be known BEFORE the confirm opens: what each item
  // holds (a folder's files), whether anything inside is in use, and the
  // register rows it would change — so the confirm says what will happen and a
  // move nobody can finish properly is never offered.
  async function startBin(list) {
    const blocked = [];
    const ready = [];
    for (const f of list) {
      const g = guardOf(f);
      if (!g.ok) { blocked.push(g.why); continue; }
      ready.push({ ...f, id: binId(f), inner: [] });
    }
    if (!ready.length) { moveToast(blocked[0] || 'Nothing to move.', { error: true, ms: 8000 }); return; }
    const hasFolder = ready.some((f) => isFolderMime(f.mimeType) && !f.shortcutId);
    const busy = hasFolder ? moveToast('Counting what is inside…', { ms: 20000 }) : null;
    try {
      for (const f of ready) {
        if (!isFolderMime(f.mimeType) || f.shortcutId) continue;
        const c = await countFolder(listFolder, f.id);
        f.count = c.count; f.truncated = c.truncated; f.inner = c.files.map((x) => x.id);
        const u = c.files.find((x) => useOf(x));
        if (u) { f.blocked = `${f.name} holds ${u.name}, which ${firstName(useOf(u).by) || 'someone'} has marked in use. Try again once it is closed.`; }
      }
      // Register rows for the files (a shortcut is not its target: none).
      const ids = ready.filter((f) => !f.shortcutId).flatMap((f) => [f.id, ...f.inner]);
      let rows = [];
      try { rows = ids.length ? await registerRowsForFiles(ids, project) : []; } catch (e) {
        busy?.close();
        moveToast(`Track couldn’t read the document register (${(e && e.message) || 'no reason given'}), so nothing was moved.`, { error: true, ms: 9000 });
        return;
      }
      const ptr = (r) => r.driveFileId || r.driveId || (String(r.driveUrl || '').match(/\/d\/([A-Za-z0-9_-]{20,})/) || String(r.driveUrl || '').match(/[?&]id=([A-Za-z0-9_-]{20,})/) || [])[1] || null;
      for (const f of ready) {
        const mine = new Set([f.shortcutId ? null : f.id, ...f.inner].filter(Boolean));
        f.rows = rows.filter((r) => mine.has(ptr(r)));
        const rb = registerBlock(f.name, f.rows, { reviewer });
        if (rb && !f.blocked) f.blocked = rb.why;
      }
    } catch (e) {
      busy?.close();
      moveToast(`Drive couldn’t list what is inside (${(e && e.message) || 'no reason given'}), so nothing was moved.`, { error: true, ms: 9000 });
      return;
    }
    busy?.close();
    const go = ready.filter((f) => !f.blocked);
    blocked.push(...ready.filter((f) => f.blocked).map((f) => f.blocked));
    if (!go.length) { moveToast(blocked[0], { error: true, ms: 9000 }); return; }
    const copy = binCopy(go);
    const registered = go.some((f) => f.rows.length);
    openEndingConfirm({
      eyebrow: 'Move to bin',
      record: copy.title,
      whatStays: [
        copy.restore,
        ...(registered ? [`Its document register entry stays, marked binned, so it no longer shows as Current. Undo puts back what it said.`] : []),
        ...blocked.map((w) => `Not moved: ${w}`),
      ],
      whatChanges: `${copy.contents ? `${copy.contents.charAt(0).toUpperCase()}${copy.contents.slice(1)}. ` : ''}${go.length === 1 ? 'It leaves' : 'They leave'} ${here.name} for everyone, in Track and in Drive. Nothing is deleted.`,
      confirmLabel: copy.confirmLabel,
      reversibleNote: 'Undo · 30 days in Drive’s bin',
      onConfirm: () => runBin(go),
    });
  }
  async function runBin(go) {
    // Asked for HERE, in the click, so Google's consent can open (Safari).
    const tokP = driveWriteToken();
    tokP.catch(() => {});
    const who = (session && session.email) || '';
    const res = await moveToBin(go, {
      trash: (id) => binDriveFile(id, tokP),
      untrash: (id) => unbinDriveFile(id),
      register: async (it) => it.rows || [],
      setStatus: (id, st) => setRegisterStatus(id, st, who),
    });
    bulk.clear();
    if (res.moved.length && items) {
      const gone = new Set(res.moved.map((m) => m.id));
      paint(items.filter((f) => !gone.has(binId(f))), lastMeta || {});
    }
    reload();
    const regNote = res.registerFailed.length ? ` · the register still says Current (${res.registerFailed[0].reason})` : '';
    const undo = res.moved.length ? () => {
      const t = driveWriteToken(); t.catch(() => {});   // in the Undo click
      res.undo().then((u) => {
        reload();
        if (u.failed.length) moveToast(`${refusalText(u.failed, 'restore')} It is still in Drive’s bin for 30 days.`, { error: true, ms: 10000 });
        else moveToast(u.restored.length === 1 ? 'Restored' : `Restored ${u.restored.length}`, { ms: 3200 });
      });
    } : null;
    if (!res.failed.length) moveToast(`${binToastText(res.moved.length)}${regNote}`, { undo, ms: 8000, error: !!regNote });
    else if (!res.moved.length) moveToast(refusalText(res.failed), { error: true, ms: 10000 });
    else moveToast(`${binToastText(res.moved.length)} · ${refusalText(res.failed)}`, { undo, error: true, ms: 10000 });
  }

  // ── The preview panel (p15) ──
  // "Mark up" is the drawing reviewer, where every PDF opens. "Compare
  // revisions", and the Checked / Issued rows, are drawn in the design but
  // Track keeps no per-document checker or issue state yet, so they stay out.
  function paintPane() {
    const pane = body.querySelector('.fl4-pane');
    if (!pane) return;
    const i = files.findIndex((f) => f.id === sel);
    const f = files[i];
    if (!f) { pane.innerHTML = `<div class="fl4-pane-empty">${files.length ? 'Select a file to see it here. Double-click to open it.' : ''}</div>`; return; }
    const s = states[i];
    const rev = revOf(f.name);
    const facts = [kindOf(f), rev ? `revision ${rev === '-' ? '–' : rev}` : '', sizeText(f.size) !== '—' ? sizeText(f.size) : ''].filter(Boolean).join(' · ');
    const u = useOf(f);
    const pdf = /\.pdf$/i.test(f.name);
    const rows = [];
    if (s) rows.push(['Register', s.tone === 'grey' ? 'Superseded' : s.tone === 'ok' ? 'Current' : `Needs renaming — ${s.label}`]);
    if (u) rows.push(['In use', `${u.by || 'Someone'}${u.app ? `, in ${u.app}` : ''}`]);
    rows.push(['Modified', `${dayShort(f.modifiedTime)}${f.lastModifyingUser && f.lastModifyingUser.displayName ? ` by ${f.lastModifyingUser.displayName}` : ''}`]);
    pane.innerHTML = `
      <div class="fl4-thumb" data-thumb="${escapeHtml(f.id)}"><span>${escapeHtml(kindOf(f))} preview</span></div>
      <div class="fl4-pane-name num-tech">${escapeHtml(f.name)}</div>
      <div class="fl4-pane-sub">${escapeHtml(facts)}</div>
      <div class="fl4-pane-btns">
        <button type="button" class="btn primary" data-pane="open">Open</button>
        ${pdf ? '<button type="button" class="btn" data-pane="markup">Mark up</button>' : ''}
        ${f.webViewLink ? `<a class="btn" href="${escapeHtml(safeUrl(f.webViewLink))}" target="_blank" rel="noopener">Open in Drive</a>` : ''}
        ${guardOf(f).ok ? '<button type="button" class="btn fl4-bin" data-bin>Move to bin</button>' : ''}
      </div>
      <dl class="fl4-facts">${rows.map(([k, v]) => `<div><dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd></div>`).join('')}</dl>`;
    pane.querySelectorAll('[data-pane]').forEach((b) => b.addEventListener('click', () => openOne(f)));
    pane.querySelector('[data-bin]')?.addEventListener('click', () => startBin([f]));
    const th = pane.querySelector('[data-thumb]');
    const put = (url) => {
      if (!url || !th.isConnected) return;
      const img = new Image();
      img.alt = '';
      img.referrerPolicy = 'no-referrer';
      img.onload = () => { if (th.isConnected) { th.innerHTML = ''; th.appendChild(img); th.classList.add('has-img'); } };
      img.src = safeUrl(url.replace(/=s\d+$/, '=s600'));
    };
    if (thumbs.has(f.id)) put(thumbs.get(f.id));
    else driveThumbnail(f.id).then((url) => { thumbs.set(f.id, url); put(url); }).catch(() => thumbs.set(f.id, null));
  }

  try {
    await swr({
      key: listKey,
      fetch: () => listFolder(here.id),
      onData: (list, meta) => paint(list, meta),
      onError: () => {
        const n = body.querySelector('#fx-fold-note');
        if (n) n.textContent = 'Drive could not be reached; this is what it showed last time.';
      },
    });
    // LIVE (07/10/2026): a file added, renamed or removed in this folder shows
    // within ~30 s while it is open — Drive's changes feed, this folder only.
    const key = listKey;
    if (body.__driveLive) body.__driveLive();
    body.__driveLive = startDriveLive({
      mount: body, who: session && session.email,
      watched: () => [here.id],
      onTouched: async () => {
        const list = await listFolder(here.id);
        if (!body.isConnected || body.__folderId !== here.id) return;
        const at = Date.now();
        driveStore.set(key, { value: list, at }).catch(() => {});
        paint(list, { cached: false, at });
        const n = body.querySelector('#fx-fold-note');
        if (n) n.textContent = 'Updated just now';
      },
    });
  } catch (e) {
    if (!body.isConnected) return;
    body.innerHTML = shell(`<div class="fl4-empty">Drive could not read this folder: ${escapeHtml((e && e.message) || 'unknown error')}. Reconnect and try again, or open it in Drive.</div>`);
    wireNav();
  }
}
